import React, { useState } from 'react';
import { TextField } from '@mui/material';
import { getUrls } from '@cliengo/mfe-utils/utils';
import { Button, Card } from '@/components/common';
import { useQueryParams } from '@/hooks/useQueryParams';
import { useWhatsAppCloudOnboarding } from '@/hooks/useWhatsAppCloudOnboarding';
import {
  WHATSAPP_MANAGER_PAYMENT_URL,
  whatsappManagerPhoneNumbersUrl,
} from '@/constants/whatsapp-cloud';

/** Alta de un número nuevo a WhatsApp Cloud API, directo con Meta (roadmap#2097). */
export const WhatsAppCloudOnboarding: React.FC = () => {
  const { returnUrl } = useQueryParams();
  const { step, error, result, wabaId, launch } = useWhatsAppCloudOnboarding();
  const [pin, setPin] = useState('');

  const goBack = () => {
    window.location.href = returnUrl || getUrls().DASH_URL;
  };

  const isBusy = step === 'signup' || step === 'connecting';

  return (
    <div className="mx-auto max-w-2xl px-4 py-8">
      <div className="mb-8 text-center">
        <h1 className="mb-4 text-2xl font-normal text-gray-800">WhatsApp Cloud API (Meta)</h1>
        <p className="text-gray-600">
          Conecta un número nuevo a la API oficial de WhatsApp, directo con Meta. El número no
          puede estar registrado en ninguna app de WhatsApp.
        </p>
      </div>

      <Card padding="large">
        {step === 'connected' && result ? (
          <div className="flex flex-col gap-4">
            <p className="text-lg text-gray-800">
              ¡Listo! El número <strong>+{result.whatsappNumber}</strong> quedó conectado.
            </p>
            <p className="text-gray-600">
              Para poder enviar mensajes, carga un método de pago en tu cuenta de WhatsApp Business:
              Meta factura los mensajes directamente a tu cuenta.
            </p>
            <div className="flex flex-wrap gap-3">
              <Button
                variant="outline"
                onClick={() => window.open(WHATSAPP_MANAGER_PAYMENT_URL, '_blank', 'noopener')}
              >
                Ir a WhatsApp Manager
              </Button>
              <Button onClick={goBack}>Volver a Cliengo</Button>
            </div>
          </div>
        ) : step === 'pin_required' ? (
          <div className="flex flex-col gap-4">
            <p className="text-gray-800">
              Este número ya tiene un PIN de verificación en dos pasos. Ingrésalo para terminar la
              conexión: tendrás que volver a confirmar la cuenta en Meta.
            </p>
            <div className="rounded-md bg-gray-50 p-4 text-sm text-gray-700">
              <p className="mb-2 font-medium">¿No recuerdas el PIN?</p>
              <p className="mb-2">Meta no lo muestra, pero puedes cambiarlo o desactivarlo:</p>
              <ol className="list-decimal space-y-1 pl-5">
                <li>
                  Abre{' '}
                  <a
                    className="text-blue-600 underline"
                    href={whatsappManagerPhoneNumbersUrl(wabaId)}
                    target="_blank"
                    rel="noopener noreferrer"
                  >
                    WhatsApp Manager
                  </a>
                  .
                </li>
                <li>
                  Elige este número y entra a <strong>Configuración</strong> →{' '}
                  <strong>Verificación en dos pasos</strong>.
                </li>
                <li>
                  Cambia el PIN y escríbelo aquí, o desactiva la verificación (Meta te enviará un
                  correo para confirmarlo) y reintenta sin PIN.
                </li>
              </ol>
            </div>
            <TextField
              label="PIN de 6 dígitos"
              value={pin}
              onChange={(event) => setPin(event.target.value.replace(/\D/g, '').slice(0, 6))}
              inputProps={{ inputMode: 'numeric' }}
            />
            <div className="flex flex-wrap gap-3">
              <Button disabled={pin.length !== 6} onClick={() => launch(pin)}>
                Continuar
              </Button>
              <Button variant="outline" onClick={() => launch()}>
                Ya la desactivé, reintentar sin PIN
              </Button>
            </div>
          </div>
        ) : (
          <div className="flex flex-col gap-4">
            {error && <p className="text-red-600">{error}</p>}
            {step === 'connecting' && <p className="text-gray-600">Conectando el número…</p>}
            <div>
              <Button loading={isBusy} disabled={isBusy} onClick={() => launch()}>
                {error ? 'Intentar de nuevo' : 'Conectar con Meta'}
              </Button>
            </div>
          </div>
        )}
      </Card>
    </div>
  );
};
